import { api } from "@/shared/api";
import {
  createTenantService,
  type ModuleItem,
  type PageResponse,
  type TenantDetail,
} from "@/entities/tenant";
import type {
  BillingBalance,
  BillingPayment,
  BillingSubscription,
  PlanSwitchPreview,
} from "@/entities/billing";

import type {
  BrandView,
  PartnerModules,
  PartnerRole,
  WalletPage,
  WalletView,
} from "./partnerAdminService";

/**
 * 渠道后台接口(OEM C-H / C-J)—— 唯一真源 = docs/plans/oem-0929/契约-渠道后台接口.md
 * + api `com.gscq.partnerportal`(api main 111123d4)。
 *
 * 全部要 PARTNER_ADMIN token;归属只取 token,请求体 / 参数里**从不带** partnerCode。
 * 按租户的接口:不归本渠道 → 404(与不存在同码);渠道停用 → 写接口 403 PARTNER_SUSPENDED。
 * 单位:1 积分 = 10000 micro = ¥0.01;金额 micro(1 元 = 1,000,000)。
 * ★渠道视图不含平台成本、毛利、上游模型名 —— 这里的类型也一个都不声明,页面就不可能误显示。
 */

import type { ApiDateTime } from "@/shared/lib/apiDateTime";

const BASE = "/partner";
const enc = encodeURIComponent;

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "" && v !== "all") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

// ── 类型 ─────────────────────────────────────────────────────────────────

/** PUT /profile/brand 白名单(与后端 PartnerProfilePortalService 同一份;后端 profile 也回 editableBrandFields)。 */
export const PARTNER_EDITABLE_BRAND_FIELDS = [
  "advisorName",
  "brainName",
  "assistantName",
  "supportText",
  "advisorGreeting",
  "loginSlogan",
] as const;
export type PartnerEditableBrandField = (typeof PARTNER_EDITABLE_BRAND_FIELDS)[number];

export interface PartnerProfile {
  code: string;
  name: string;
  status: string;
  /** 渠道自己的 brand_json(不含直营兜底)+ 品牌图预览 URL。 */
  brand: BrandView | null;
  /** 外链模块(外呼 / 旧 GEO / 漫剧),没配 = false。 */
  modules: PartnerModules | null;
  /** 可售功能模块;null = 不限。 */
  sellableModules: string[] | null;
  /** 可售套餐;null = 不限。 */
  allowedPlanCodes: string[] | null;
  impersonationEnabled: boolean;
  walletBalanceCredits: number;
  editableBrandFields: string[] | null;
}

/** 可售套餐(已按白名单过滤;creditsPerCycle = 一个周期折多少积分,已含渠道折扣)。 */
export interface PartnerPlan {
  planCode: string;
  displayName: string;
  billingCycle: string;
  priceMicro: number;
  creditsPerCycle: number;
  modulesJson: string | null;
}

/** 可售模块(已按 sellable_modules 过滤)。 */
export interface PartnerSellableModule {
  capability: string;
  displayName: string;
}

/** 行业目录行(GET /partner/industries,启用行业;与平台 / 租户端同一份 xb_industry)。 */
export interface PartnerIndustry {
  code: string;
  name: string;
}

export interface PartnerCreateTenantRequest {
  name: string;
  email: string;
  contact?: string;
  phone?: string;
  industryCode?: string;
  planCode: string;
  address?: string;
  scale?: string;
  remark?: string;
  // ★OEM 第二批 A2:不再有 modules —— 开哪些舱只由套餐 + 增值舱决定,后端带 modules 非空 → 400。
}

/** 渠道改资料:没有 plan / expiryAt(那两样走计费口扣钱包),带了后端整单 400。 */
export interface PartnerUpdateTenantRequest {
  name?: string;
  industryCode?: string;
  contact?: string;
  phone?: string;
  email?: string;
  address?: string;
  scale?: string;
  remark?: string;
}

export interface PartnerImpersonateResult {
  accessToken: string;
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  expiresInMs: number;
}

/** 报价分项(OEM 第二批 A3):续费带增值舱时逐舱列出。 */
export interface QuoteItem {
  type: "PLAN" | "ADDON" | (string & {});
  code: string;
  credits: number;
}

export interface QuoteView {
  action: "activate" | "renew" | string;
  planCode: string;
  billingCycle: string;
  termCycles: number;
  priceMicroPerCycle: number;
  discountBp: number;
  credits: number;
  walletBalance: number;
  sufficient: boolean;
  /** 分项(PLAN / ADDON);老后端没有。 */
  items?: QuoteItem[] | null;
}

/**
 * 换档将失效的增值舱(OEM 第二批 A3)。reason:PLAN_INCLUDES = 新套餐自带(按剩余退回);
 * NOT_ELIGIBLE = 新套餐不能加购 = 降档,确认时必须带 acknowledgeAddonRetire。
 */
export interface AddonRetireItem {
  capability: string;
  addonCode: string;
  reason: "PLAN_INCLUDES" | "NOT_ELIGIBLE" | (string & {});
  refundCredits: number;
  refundMicro: number;
  quotaReclaim: number;
}

export interface PartnerSwitchDiff extends PlanSwitchPreview {
  addonsToRetire?: AddonRetireItem[] | null;
}

export interface SwitchPreviewView {
  diff: PartnerSwitchDiff;
  /** false = 非 active 订阅,只改套餐码不扣钱。 */
  charged: boolean;
  credits: number;
  /** 差价按几份算(= 本代补额度份数;非在用订阅 0)。 */
  shares?: number;
  walletBalance: number;
}

export interface BillingActionResult {
  /** false = 同一 requestId 重放(没再扣钱、没再执行)。 */
  applied: boolean;
  chargedCredits: number;
  walletBalanceAfter: number;
  walletEventId: number | null;
}

// ── 增值舱(OEM 第二批 A3)──────────────────────────────────────────────

/** 一个 SKU。yearlyCredits = 一年牌价折合渠道积分(增值舱按牌价,不打折)。 */
export interface AddonSku {
  addonCode: string;
  capability: string;
  displayName: string;
  description: string | null;
  priceMicro: number;
  yearlyCredits: number;
  bucketKey: string | null;
  perSeatAmount: number | null;
  benefitJson: string | null;
  status: string;
  sort: number | null;
  /** 舱目录状态(active / coming_soon …);非 active 不可售。 */
  moduleStatus: string | null;
}

/** 中途加购报价:days = 剩余天数,billDays = 计费天数(最低 30)。 */
export interface AddonQuote {
  addonCode: string;
  capability: string;
  endAt: string | null;
  days: number;
  billDays: number;
  priceMicro: number;
  credits: number;
  quotaTarget: number;
  walletBalance: number | null;
  sufficient: boolean | null;
}

export interface AddonHolding {
  id: number;
  capability: string;
  addonCode: string | null;
  /** ACTIVE / EXPIRED / RETIRED / REFUNDED */
  status: string;
  /** PARTNER_WALLET / DIRECT_PAID / GIFT */
  source: string;
  partnerCode: string | null;
  startAt: string | null;
  endAt: string | null;
  seatsSnapshot: number | null;
  perSeatAmountSnapshot: number | null;
  priceMicroSnapshot: number | null;
  reason: string | null;
}

export interface AddonOrder {
  id: number;
  capability: string;
  addonCode: string | null;
  action: string;
  chargeMode: string | null;
  generation: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  days: number | null;
  amountMicro: number;
  walletEventId: number | null;
  partnerCode: string | null;
  quotaDelta: number | null;
  remark: string | null;
  operator: string | null;
  createdAt: string | null;
}

/** 某 SKU 对该租户能不能加购:不能时 reason = 错误码名,reasonText = 中文说明;能时带 quote。 */
export interface AddonPurchasable {
  sku: AddonSku;
  purchasable: boolean;
  reason: string | null;
  reasonText: string | null;
  quote: AddonQuote | null;
}

export interface TenantAddons {
  tenantCode: string;
  planCode: string | null;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  holdings: AddonHolding[];
  orders: AddonOrder[];
  catalog: AddonPurchasable[];
}

/** 渠道视图的模块行(A2 起带 ownedBy / paused)。 */
export interface PartnerModuleItem extends ModuleItem {
  /** PLAN / ADDON / null(未拥有)。 */
  ownedBy?: "PLAN" | "ADDON" | (string & {}) | null;
  paused?: boolean;
  pausedReason?: string | null;
}

/** 缴费合并视图(A3):套餐行 + 增值舱行。 */
export interface PartnerPayment extends BillingPayment {
  itemType?: "PLAN" | "ADDON" | (string & {}) | null;
  capability?: string | null;
  addonCode?: string | null;
  chargeMode?: string | null;
}

// ── 租户管理员(B6)/ 渠道子账号(A4)───────────────────────────────────

export interface TenantAdminAccount {
  id: number;
  displayName: string | null;
  maskedUsername: string | null;
  /** 登录名原文(1004:登录名不脱敏,只脱敏联系方式;老后端不带 → 回退 maskedUsername)。 */
  username?: string | null;
  maskedEmail: string | null;
  maskedPhone: string | null;
  enabled: boolean;
  lastLoginAt: string | null;
}

export interface TenantAdminPasswordReset {
  principalId: number;
  tenantCode: string;
  tempPassword: string;
}

export interface PartnerAccount {
  id: number;
  partnerCode: string;
  username: string | null;
  email: string | null;
  displayName: string | null;
  phone: string | null;
  enabled: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
  partnerRole: PartnerRole | (string & {}) | null;
}

export interface CreatePartnerAccountRequest {
  username?: string;
  displayName?: string;
  email: string;
  phone?: string;
  partnerRole: PartnerRole;
}

export interface UpdatePartnerAccountRequest {
  displayName?: string;
  phone?: string;
  email?: string;
  enabled?: boolean;
  partnerRole?: PartnerRole;
}

/** GET /me 里渠道相关的两项(A4)。partnerPerms 缺省 = 老后端(不分角色)。 */
export interface PartnerMe {
  userId?: number;
  partnerRole?: string | null;
  partnerPerms?: string[] | null;
}

export interface CreditMoveResult {
  applied: boolean;
  walletBalanceAfter: number;
  walletEventId: number | null;
}

/** 渠道流水行:后端已抹掉成本与模型名(sumCostMicro / primaryModel 恒 null),这里干脆不声明。 */
export interface PartnerLedgerEvent {
  eventId: number | null;
  eventType: string;
  actionType: string | null;
  idempotencyKey: string | null;
  tenantCode: string;
  bucketKey: string;
  generation: number | null;
  deltaUnits: number | null;
  balanceAfter: number | null;
  callsCount: number;
  sumInputTokens: number;
  sumOutputTokens: number;
  sumUnits: number;
  featureCode: string | null;
  occurredAt: string | null;
}

export interface LedgerFilter {
  tenantCode: string;
  eventType?: string;
  actionType?: string;
  from?: string;
  to?: string;
}

// 用量(不出成本、不按模型拆)
export interface PartnerUsageSummary {
  totalCalls: number;
  successCalls: number;
  totalTokens: number;
  totalInput: number;
  totalOutput: number;
  totalCredits: number;
}

export interface PartnerUsagePoint {
  bucket: string;
  /** groupBy=feature 时是 capability;tenant 时是租户 code。 */
  dimension: string;
  calls: number;
  tokens: number;
  credits: number;
}

export interface PartnerUsageTopRow {
  key: string;
  metricValue: number;
  calls: number;
  tokens: number;
  credits: number;
}

export type UsageMetric = "credits" | "tokens" | "calls";
export type UsageDim = "tenant" | "feature";

export interface UsageRange {
  from: string;
  to: string;
}

export interface PartnerAuditRow {
  id: number;
  operator: string | null;
  role: string | null;
  resource: string | null;
  resourceId: string | null;
  targetTenantCode: string | null;
  action: string | null;
  remoteAddr: string | null;
  occurredAt: string | null;
  /** 1004 O4:代客动作的原因(其余动作 null)。 */
  reason?: string | null;
}

// 对账单(只 CONFIRMED / PAID;不含平台收入 / 成本 / 毛利)
export type SettlementStatus = "CONFIRMED" | "PAID";

export interface SettlementStatement {
  period: string;
  status: SettlementStatus | string;
  commissionBp: number | null;
  tenantCount: number | null;
  settleCredits: number | null;
  refundCredits: number | null;
  crossMonthRefundCredits: number | null;
  netConsumedCredits: number | null;
  commissionableCredits: number | null;
  nonCommissionableCredits: number | null;
  cumCommissionableCredits: number | null;
  commissionableIncrementCredits: number | null;
  accrualMicro: number | null;
  carryInMicro: number | null;
  payableMicro: number | null;
  carryOutMicro: number | null;
  breakdownJson: string | null;
  confirmedAt: string | null;
  paidAmountMicro: number | null;
  paidVoucher: string | null;
  paidAt: string | null;
}

export interface SettlementLine {
  tenantCode: string;
  generation: number | null;
  selfUse: number | null;
  settleCredits: number | null;
  refundCredits: number | null;
  crossMonthRefundCredits: number | null;
  netConsumedCredits: number | null;
  commissionableCredits: number | null;
  nonCommissionableCredits: number | null;
  cumCommissionableCredits: number | null;
  accrualMicro: number | null;
  breakdownJson: string | null;
}

/** 撤销记录(B4):平台撤销过打款 / 确认时渠道能看到。 */
export interface SettlementReversal {
  /** UNPAY(撤销打款登记)| UNCONFIRM(撤销确认) */
  kind: string;
  fromStatus: string | null;
  toStatus: string | null;
  reason: string | null;
  createdAt: string | null;
}

export interface SettlementDetail {
  statement: SettlementStatement;
  lines: SettlementLine[];
  reversals?: SettlementReversal[] | null;
}

// ── Service ──────────────────────────────────────────────────────────────

/** 与平台端同一套租户读写(basePath 注入),只暴露渠道后端真有的方法。 */
const tenantRw = createTenantService(`${BASE}/tenants`);

/**
 * 渠道看自己的年费(OEM 第二批 A7,GET /partner/annual-fees,要 ANNUAL_FEE:OWNER / FINANCE)。按年倒序。
 * 不含收款渠道 / 凭证号 / 钱包流水 id。日期生产应为 ISO 串,前端也认数组。
 */
export interface PartnerAnnualFee {
  feeYear: number;
  amountMicro: number;
  /** PENDING / PAID / WAIVED */
  status: "PENDING" | "PAID" | "WAIVED" | (string & {});
  periodStart: ApiDateTime;
  periodEnd: ApiDateTime;
  paidAt: ApiDateTime;
  remark: string | null;
}

/**
 * 渠道后台首页提醒(1002,`GET /partner/reminders`)。三条各自 null = 没有需要提醒的 / 当前角色无权看
 * (余额要 WALLET_BALANCE、到期要 TENANT_READ、月结单要 SETTLEMENT;后端裁剪,前端只管有就显示)。
 */
export interface PartnerReminders {
  walletLow: { balanceCredits: number; thresholdCredits: number | null } | null;
  expiringTenants: { count: number; withinDays: number } | null;
  unviewedSettlements: { count: number; periods: string[] } | null;
}

export const partnerPortalService = {
  // 渠道自身
  profile: () => api.get<PartnerProfile>(`${BASE}/profile`),
  /** 首页提醒条(1002)。 */
  reminders: () => api.get<PartnerReminders>(`${BASE}/reminders`),
  /** 只收白名单字段;null / 空串 = 清空(回落默认)。 */
  updateBrand: (fields: Partial<Record<PartnerEditableBrandField, string | null>>) =>
    api.put<PartnerProfile>(`${BASE}/profile/brand`, fields),
  wallet: () => api.get<WalletView>(`${BASE}/wallet`),
  walletEvents: (params: { page?: number; pageSize?: number; type?: string } = {}) =>
    api.get<WalletPage>(
      `${BASE}/wallet/events${query({
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        type: params.type,
      })}`
    ),
  plans: () => api.get<PartnerPlan[]>(`${BASE}/plans`),
  modules: () => api.get<PartnerSellableModule[]>(`${BASE}/modules`),
  /** 行业目录(OEM F3):列表 / 详情把 IND_* 编码显示成行业名。 */
  industries: () => api.get<PartnerIndustry[]>(`${BASE}/industries`),

  // 租户
  listTenants: (
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      plan?: string;
    } = {}
  ) =>
    tenantRw.list({
      ...params,
      status: params.status === "all" ? undefined : params.status,
      plan: params.plan === "all" ? undefined : params.plan,
    }),
  tenantSummary: () => tenantRw.summary(),
  createTenant: (req: PartnerCreateTenantRequest) => api.post<TenantDetail>(`${BASE}/tenants`, req),
  getTenant: (id: number | string) => tenantRw.get(id),
  updateTenant: (id: number | string, req: PartnerUpdateTenantRequest) =>
    api.put<TenantDetail>(`${BASE}/tenants/${id}`, req),
  updateTenantStatus: (id: number | string, status: "active" | "suspended", reason?: string) =>
    api.put<TenantDetail>(`${BASE}/tenants/${id}/status`, { status, reason: reason || undefined }),
  tenantModules: (id: number | string) =>
    api.get<PartnerModuleItem[]>(`${BASE}/tenants/${id}/modules`),
  /**
   * 暂停 / 恢复一个**已拥有**的舱(OEM 第二批 A2;旧的整表 PUT /modules 已删 = 405)。
   * 未拥有 → 409 MODULE_NOT_OWNED。
   */
  pauseTenantModule: (
    id: number | string,
    capability: string,
    req: { paused: boolean; reason?: string }
  ) =>
    api.put<PartnerModuleItem[]>(`${BASE}/tenants/${id}/modules/${enc(capability)}/pause`, {
      paused: req.paused,
      reason: req.reason?.trim() || undefined,
    }),
  /** 该租户的管理员账号(联系方式已脱敏)。 */
  tenantAdmins: (id: number | string) =>
    api.get<TenantAdminAccount[]>(`${BASE}/tenants/${id}/admins`),
  /** 重置租户管理员密码:临时口令只在这次返回;对方首登强制改密、旧会话全撤。 */
  resetTenantAdminPassword: (id: number | string, principalId: number) =>
    api.post<TenantAdminPasswordReset>(
      `${BASE}/tenants/${id}/admins/${principalId}/reset-password`,
      null
    ),
  tenantQuotas: (id: number | string) => api.get<ModuleItem[]>(`${BASE}/tenants/${id}/quotas`),
  tenantTimeline: (id: number | string, eventType = "all") => tenantRw.getTimeline(id, eventType),
  /** 平台没给本渠道开代客 → 403。 */
  /** 1005:代客原因(≥4 字)随请求体送后端写审计;api 对所有代客强制原因。 */
  impersonate: (code: string, reason?: string) =>
    api.post<PartnerImpersonateResult>(
      `${BASE}/tenants/${enc(code)}/impersonate`,
      reason ? { reason } : null
    ),

  // 计费(从渠道钱包扣)
  /**
   * addons(只对 renew 有意义):undefined = 后端默认(在用付费舱 + 与上期同时到期的);
   * [] = 都不续(显式发空串);其余逗号拼接。
   */
  quote: (
    code: string,
    action: "activate" | "renew",
    termCycles: number,
    planCode?: string,
    addons?: string[]
  ) => {
    const base = `${BASE}/billing/tenants/${enc(code)}/quote${query({ action, planCode, termCycles })}`;
    if (addons === undefined) return api.get<QuoteView>(base);
    return api.get<QuoteView>(`${base}&addons=${enc(addons.join(","))}`);
  },
  /**
   * 换套餐报价(api 106577c5):action=switch&planCode=,与预览 / 实扣同一套差价算法。
   * 不带 termCycles —— 份数由服务端按本代已买时长定(回包 termCycles = 份数);非在用订阅 credits=0。
   */
  switchQuote: (code: string, planCode: string) =>
    api.get<QuoteView>(
      `${BASE}/billing/tenants/${enc(code)}/quote${query({ action: "switch", planCode })}`
    ),
  activate: (code: string, req: { planCode: string; termCycles: number; requestId: string }) =>
    api.post<BillingActionResult>(`${BASE}/billing/tenants/${enc(code)}/activate`, req),
  /** addons:undefined = 后端默认;[] = 都不续。每舱单独扣一次钱包,与套餐同一事务。 */
  renew: (code: string, req: { termCycles: number; requestId: string; addons?: string[] }) =>
    api.post<BillingActionResult>(`${BASE}/billing/tenants/${enc(code)}/renew`, req),
  /**
   * 换套餐(0929 拍板「按差价」):渠道只开 KEEP_EXPIRY —— 沿用原到期日,升档按差价扣、降档 / 平价扣 0 不退;
   * RESTART_CYCLE 后端 400。termCycles 服务端忽略,不再发。预览只用来看权益 diff,金额以 switchQuote 为准。
   */
  switchPlanPreview: (code: string, planCode: string) =>
    api.get<SwitchPreviewView>(
      `${BASE}/billing/tenants/${enc(code)}/switch-plan/preview${query({
        planCode,
        mode: "KEEP_EXPIRY",
      })}`
    ),
  /** 同一 requestId 并发 / 重放 → 200 applied=false(不重复扣)。 */
  /** 降档会让增值舱失效时须 acknowledgeAddonRetire=true,否则 400。 */
  switchPlan: (
    code: string,
    req: { planCode: string; requestId: string; acknowledgeAddonRetire?: boolean }
  ) =>
    api.post<BillingActionResult>(`${BASE}/billing/tenants/${enc(code)}/switch-plan`, {
      planCode: req.planCode,
      requestId: req.requestId,
      mode: "KEEP_EXPIRY",
      ...(req.acknowledgeAddonRetire ? { acknowledgeAddonRetire: true } : {}),
    }),
  payments: (code: string) =>
    api.get<PartnerPayment[]>(`${BASE}/billing/tenants/${enc(code)}/payments`),

  // 增值舱(OEM 第二批 A3;按牌价,不打折)
  /** 本渠道可售增值舱目录(sellable_addons ∩ active)。 */
  addons: () => api.get<AddonSku[]>(`${BASE}/addons`),
  /** 租户增值舱面板:持有 / 流水 / 每个可售 SKU 能否加购。 */
  tenantAddons: (code: string) =>
    api.get<TenantAddons>(`${BASE}/billing/tenants/${enc(code)}/addons`),
  /** 加购报价;不可买直接回对应错误码。 */
  addonQuote: (code: string, addonCode: string) =>
    api.get<AddonQuote>(`${BASE}/billing/tenants/${enc(code)}/addons/quote${query({ addonCode })}`),
  /** 中途加购(扣渠道钱包);同一 requestId 重放 / 并发 → 200 applied=false。 */
  buyAddon: (code: string, req: { addonCode: string; requestId: string }) =>
    api.post<BillingActionResult>(`${BASE}/billing/tenants/${enc(code)}/addons`, req),

  // 渠道身份 / 子账号(OEM 第二批 A4)
  /** GET /me:partnerRole + partnerPerms(前端据此过滤导航与按钮;服务端每个接口仍独立判权)。 */
  me: () => api.get<PartnerMe>("/me"),
  accounts: () => api.get<PartnerAccount[]>(`${BASE}/accounts`),
  createAccount: (req: CreatePartnerAccountRequest) =>
    api.post<{ admin: PartnerAccount; initialPassword: string | null }>(`${BASE}/accounts`, req),
  updateAccount: (id: number, req: UpdatePartnerAccountRequest) =>
    api.put<PartnerAccount>(`${BASE}/accounts/${id}`, req),
  resetAccountPassword: (id: number) =>
    api.post<{ initialPassword: string }>(`${BASE}/accounts/${id}/reset-password`, null),
  deleteAccount: (id: number) => api.delete<{ success: boolean }>(`${BASE}/accounts/${id}`),
  subscriptions: (code: string) =>
    api.get<BillingSubscription[]>(`${BASE}/billing/subscriptions${query({ tenantCode: code })}`),
  balances: (code: string) =>
    api.get<BillingBalance[]>(`${BASE}/billing/balances${query({ tenantCode: code })}`),
  ledger: (f: LedgerFilter & { page?: number; size?: number }) =>
    api.get<PageResponse<PartnerLedgerEvent>>(
      `${BASE}/billing/ledger${query({
        tenantCode: f.tenantCode,
        eventType: f.eventType,
        actionType: f.actionType,
        from: f.from,
        to: f.to,
        page: f.page ?? 1,
        size: f.size ?? 20,
      })}`
    ),
  ledgerExportPath: (f: LedgerFilter) =>
    `${BASE}/billing/ledger/export${query({
      tenantCode: f.tenantCode,
      eventType: f.eventType,
      actionType: f.actionType,
      from: f.from,
      to: f.to,
    })}`,

  // 积分划拨 / 回收
  transferCredits: (code: string, req: { credits: number; reason?: string; requestId: string }) =>
    api.post<CreditMoveResult>(`${BASE}/tenants/${enc(code)}/credits/transfer`, req),
  /** 超过净划拨 → 400;租户余额不足 → 409。 */
  reclaimCredits: (code: string, req: { credits: number; reason?: string; requestId: string }) =>
    api.post<CreditMoveResult>(`${BASE}/tenants/${enc(code)}/credits/reclaim`, req),

  // 用量
  usageSummary: (r: UsageRange & { tenant?: string; feature?: string }) =>
    api.get<PartnerUsageSummary>(`${BASE}/usage/summary${query({ ...r })}`),
  usageSeries: (
    r: UsageRange & { tenant?: string; groupBy?: UsageDim; granularity?: "day" | "hour" }
  ) =>
    api.get<PartnerUsagePoint[]>(
      `${BASE}/usage/series${query({
        from: r.from,
        to: r.to,
        tenant: r.tenant,
        groupBy: r.groupBy ?? "feature",
        granularity: r.granularity ?? "day",
      })}`
    ),
  usageTop: (r: UsageRange & { metric?: UsageMetric; dim?: UsageDim; limit?: number }) =>
    api.get<PartnerUsageTopRow[]>(
      `${BASE}/usage/top${query({
        from: r.from,
        to: r.to,
        metric: r.metric ?? "credits",
        dim: r.dim ?? "tenant",
        limit: r.limit ?? 10,
      })}`
    ),

  // 审计(本渠道)
  audit: (
    p: {
      operator?: string;
      resource?: string;
      action?: string;
      targetTenantCode?: string;
      from?: string;
      to?: string;
      page?: number;
      size?: number;
    } = {}
  ) =>
    api.get<PageResponse<PartnerAuditRow>>(
      `${BASE}/audit${query({ ...p, page: p.page ?? 1, size: p.size ?? 20 })}`
    ),

  // 对账单
  annualFees: () => api.get<PartnerAnnualFee[]>(`${BASE}/annual-fees`),
  settlements: (year?: number) =>
    api.get<SettlementStatement[]>(`${BASE}/settlements${query({ year })}`),
  settlement: (period: string) => api.get<SettlementDetail>(`${BASE}/settlements/${enc(period)}`),
  settlementExportPath: (period: string) => `${BASE}/settlements/${enc(period)}/export`,
};
